// contains data for Compare Popup
import * as log from 'loglevel';

import { createAction, createReducer } from '@reduxjs/toolkit';
import {
  addToBasket, fetchProductsStockAvailability, fetchProductsDataWithTechnicalSpecifications,
  fetchProductsDataFromSearchLabsByProductIds
} from '../dataFetcher/dataFetcher';
import { getBasicInfoDataFromStoreAPIForCompareProducts } from '../dataConverters/comparePopupConverter';

// 1. Initial state
const initialState = {
  // determine if data for a comare popup are loading or not
  loading: false,

  // products' data of products which are displayed in the popup
  compareProductsData: undefined,

  // timestamp of last request for gathering data for compare popup
  // it's used for guaranteeing that data, for the latest user's action to show popup, are shown in the popup
  // Data from responses which requests were started before this timestamp are thrown away
  lastRequestStartTime: null,

  // when product is adding to basket by clicking 'addtobasket' button in compare popup, this property contains id of
  // product adding to basket and request status information and eventually an error message when adding to basket has failed
  // if it is null, no product is adding to basket at that moment
  addingProductToBasket: null
};

// 2. Basic actions creators
const startProductsDataRequest = createAction('comparePopup/startProductsDataRequest');
const onProductsDataResponse = createAction('comparePopup/onProductsDataResponse');
const onProductsDataRequestFailure = createAction('comparePopup/onProductsDataRequestFailure');
const setAddingProductFromPopupToBasketState = createAction('comparePopup/setAddingProductFromPopupToBasketState');
export const removeProductFromPopup = createAction('comparePopup/removeProductFromPopup');
export const removeAllProductsFromPopup = createAction('comparePopup/removeAllProductsFromPopup');


// 3. Complex actions which call basic actions above
export const getComparePopupProducts = (productIds = []) => async (dispatch, getState) => {
  const currentRequestStartTime = Date.now();
  dispatch(startProductsDataRequest(currentRequestStartTime));

  // getting products data from Store API
  const productsWithTechSpecificationsResponse =
    await fetchProductsDataWithTechnicalSpecifications(productIds, currentRequestStartTime);

  // We care only about the latest request by user. Responses of earlier requests are thrown away.
  if (productsWithTechSpecificationsResponse.requestStartTime === getState().listingPage.comparePopup.lastRequestStartTime) {

    if (!productsWithTechSpecificationsResponse.ok) {
      log.error(productsWithTechSpecificationsResponse.customErrorMessage);

      dispatch(onProductsDataRequestFailure(productsWithTechSpecificationsResponse.customErrorMessage));

      return;
    }

    const productIdsToCheckStock =
      productsWithTechSpecificationsResponse.products.filter((product) => product.checkInStock).map((product) => product.id);

    let productsStockResponse;
    if (productIdsToCheckStock.length > 0) {
      // getting products stock from Store API
      productsStockResponse = await fetchProductsStockAvailability(productIdsToCheckStock);

      if (!productsStockResponse.ok) {
        log.error(productsStockResponse.customErrorMessage);

        dispatch(onProductsDataRequestFailure(productsStockResponse.customErrorMessage));

        return;
      }
    }

    const searchLabsResponse = await fetchProductsDataFromSearchLabsByProductIds(productIds);

    if (!searchLabsResponse.ok) {
      log.error(productsStockResponse.customErrorMessage);

      dispatch(onProductsDataRequestFailure(searchLabsResponse.customErrorMessage));

      return;
    }

    dispatch(onProductsDataResponse(
      {
        productsBasicInfo: getBasicInfoDataFromStoreAPIForCompareProducts(
          productsWithTechSpecificationsResponse.products,
          productsStockResponse.storeStockAPIData.payload,
          searchLabsResponse.searchLabsResponseData.resultsets.default.results
        ),
        productsTechnicalSpecificationsInfo: productsWithTechSpecificationsResponse.products.map(
          (product) => product.technicalSpecifications
        )
      }
    ));
  } else {
    log.info(`The data for Compare Popup from response for products with IDs ${productIds.join(',')} has 
    been ignore because user requested for the popup for different products later.`);
  }

};

export function addProductToBasketFromComparePopup(productId) {
  return async (dispatch) => {

    dispatch(setAddingProductFromPopupToBasketState({
      productId,
      inProgress: true
    }));

    const addToBasketResult = await addToBasket({ fupid: productId, quantity: 1 });

    // redirect to step page if product was added to basket
    if (addToBasketResult.ok) {
      log.info(`Product with id: ${productId} successfully added to basket.
        Redirecting to step page ${addToBasketResult.redirectUrl}`);
      dispatch(setAddingProductFromPopupToBasketState(null));

      window.location.href = addToBasketResult.redirectUrl;
    } else {
      log.error(addToBasketResult.customErrorMessage);

      dispatch(setAddingProductFromPopupToBasketState({
        productId,
        inProgress: false,
        errorMessage: addToBasketResult.errorMessageForGUI
      }));
    }
  };
}

// 4. Reducer
/* eslint-disable no-param-reassign */
export default createReducer(initialState, {
  [removeProductFromPopup]: (stateDraft, action) => {
    if (stateDraft.compareProductsData.productsBasicInfo.length === 1) {
      // there is only one product in compareProductsData so we remove the whole content of compareProductsData
      stateDraft.compareProductsData = undefined;

      return;
    }

    const productOrderToRemove = action.payload;
    stateDraft.compareProductsData.productsBasicInfo =
      stateDraft.compareProductsData.productsBasicInfo.filter((item, index) => (index !== productOrderToRemove));

    stateDraft.compareProductsData.productsTechnicalSpecificationsInfo =
      stateDraft.compareProductsData.productsTechnicalSpecificationsInfo.filter((item, index) =>
        (index !== productOrderToRemove));
  },
  [removeAllProductsFromPopup]: (stateDraft) => {
    stateDraft.compareProductsData = undefined;
  },
  [startProductsDataRequest]: (stateDraft, action) => {
    stateDraft.lastRequestStartTime = action.payload;
    stateDraft.loading = true;
    stateDraft.error = null;
    stateDraft.addingProductToBasket = null;
  },
  [onProductsDataResponse]: (stateDraft, action) => {
    stateDraft.compareProductsData = action.payload;
    stateDraft.loading = false;
    stateDraft.error = null;
  },
  [onProductsDataRequestFailure]: (stateDraft, action) => {
    stateDraft.compareProductsData = undefined;
    stateDraft.loading = false;
    stateDraft.error = action.payload;
  },
  [setAddingProductFromPopupToBasketState]: (stateDraft, action) => {
    stateDraft.addingProductToBasket = action.payload;
  }
});
